import React, { useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Print } from "@mui/icons-material";
import CustomDataGrid from '../../../../../components/ui/customDataGrid/customDataGrid.component';
import { EQUIPMENT_COUNTERS_ENDPOINT } from '../../../../../consts';
import { useHasPermission } from '../../../../../contexts/auth';
import { formatCounterProvider, formatDateTime, formatNumber } from '../../../../../utils';

export const EquipmentCounters = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canViewCounters = useHasPermission("settlement.view_equipmentcounter");
  const [selectedTab, setSelectedTab] = useState(0);

  const handleRowClick = (counterId: string) => {
    navigate(`/equipment-section/equipments/${id}/counters/${counterId}`);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "id",
      headerName: "ID",
    },
    {
      field: "provider",
      headerName: t("Provider"),
      valueGetter: (value, row) =>
        formatCounterProvider(row.provider_display, row.email_source_display),
    },
    {
      field: "value",
      headerName: t("Value"),
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "read_at",
      headerName: t("Read at"),
      valueFormatter: (value) => formatDateTime(value) || "-",
    },
    {
      field: "verified_at",
      headerName: t("Verified at"),
      valueFormatter: (value) => formatDateTime(value) || "-",
    },
  ], [t]);

  if (!canViewCounters) {
    return null;
  }

  const counterTabs = [
    { key: 'black', label: t("Black pages"), kind: "BP" },
    { key: 'color', label: t("Color pages"), kind: "CP" },
    { key: 'scanned', label: t("Scanned pages"), kind: "SP" },
  ];

  const currentTab = counterTabs[selectedTab];
  const countersUrl = id && currentTab
    ? `${EQUIPMENT_COUNTERS_ENDPOINT}?equipment=${id}&kind=${currentTab.kind}`
    : EQUIPMENT_COUNTERS_ENDPOINT;

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {counterTabs.map((tab) => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </Tabs>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          mt: 2,
        }}
      >
        <CustomDataGrid
          icon={<Print />}
          title={currentTab?.label}
          url={countersUrl}
          gridSettingsKey="EQUIPMENT_COUNTERS"
          columns={columns}
          refreshEventName="equipmentCounterAdded"
          onRowClick={handleRowClick}
        />
      </Box>
    </Box>
  );
};
